import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import Input from "../../../ui/common/Input";
import Select from "../../../ui/select/Select";
import ComboSelect from "../../../ui/select/ComboSelect";
import Button from "../../../ui/common/Button";
import { EditIcon, DeleteIcon, PlusIcon, SaveIcon, CloseIcon } from "../../../../icons/Icons";
import QuestionMarkIcon from "../../../../icons/QuestionMarkIcon";
import Api from "../../../../store/api/Api";
import "./ArticleValueModal.scss";

interface SubSection {
  label: string;
  path: string;
  format: 'number' | 'percentage' | 'time';
  dataSource: string;
}

interface Section {
  label: string;
  minRange: number;
  maxRange: number;
  path: string;
  maxPath: string;
  minPath: string;
  dataSource: string;
  subSections: SubSection[];
}

interface ArticleValueConfig {
  totalScorePath: string;
  sections: Section[];
}

interface ArticleValueDataWrapper {
  default?: ArticleValueConfig;
  value?: ArticleValueConfig;
}

interface ArticleValueModalProps {
  brandId: string;
  brandName: string;
  data: ArticleValueConfig | ArticleValueDataWrapper;
  onSave: (data: ArticleValueConfig) => void;
  onClose: () => void;
  hideActions?: boolean;
}

const ArticleValueModal = ({ brandId, brandName, data, onSave, onClose, hideActions = false }: ArticleValueModalProps) => {

  const emptySubSection: SubSection = {
    label: '',
    path: '',
    format: 'number',
    dataSource: ''
  };

  const emptySection: Section = {
    label: '',
    minRange: 1,
    maxRange: 10,
    path: '',
    maxPath: '',
    minPath: '',
    dataSource: '',
    subSections: []
  };

  const [sectionEditIndex, setSectionEditIndex] = useState<number | null>(null);
  const [sectionEditData, setSectionEditData] = useState<Section>(emptySection);
  const [subsectionEditMode, setSubsectionEditMode] = useState(false);
  const [subsectionParentIndex, setSubsectionParentIndex] = useState<number | null>(null);
  const articleValueData = (data as ArticleValueDataWrapper)?.value || data as ArticleValueConfig;
  const [config, setConfig] = useState<ArticleValueConfig>({
    totalScorePath: articleValueData?.totalScorePath || '',
    sections: articleValueData?.sections || []
  });
  const [error, setError] = useState<string | null>(null);
  const [availablePaths, setAvailablePaths] = useState<string[]>([]);
  const [totalScorePathSaved, setTotalScorePathSaved] = useState(false);
  const [sectionSaved, setSectionSaved] = useState(false);
  const [subsectionSaved, setSubsectionSaved] = useState(false);
  const [bulkDataSource, setBulkDataSource] = useState('');
  const [bulkDataSourceSaved, setBulkDataSourceSaved] = useState(false);
  const sectionBodyRef = useRef<HTMLDivElement>(null);
  const api = useMemo(() => new Api(), []);

  const formatOptions = useMemo(() => [
    { key: 'number', value: 'number', label: 'number' },
    { key: 'percentage', value: 'percentage', label: 'percentage' },
    { key: 'time', value: 'time', label: 'time' }
  ], []);

  // Extract all paths from nested object
  const extractPaths = useCallback((obj: any, prefix = ''): string[] => {
    let paths: string[] = [];
    for (const key in obj) {
      const path = prefix ? `${prefix}.${key}` : key;
      if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
        paths = paths.concat(extractPaths(obj[key], path));
      } else {
        paths.push(path);
      }
    }
    return paths;
  }, []);

  // Fetch available paths from API
  useEffect(() => {
    const fetchAvailablePaths = async () => {
      setError(null);
      try {
        const latestPost = await api.getLatestPost(brandId);
        
        if (!latestPost || !latestPost.relationId) {
          setError('No articles found. The dropdown options are dynamically generated from your latest article data. Please publish an article first to see available paths.');
          return;
        }
        
        try {
          const numbers = await api.getStoryNumbers(brandId, latestPost.relationId);
          
          if (!numbers || Object.keys(numbers).length === 0) {
            setError('No traffic data available yet. The dropdown options are dynamically generated from article metrics. Please wait for the latest article to receive traffic before configuring paths.');
            return;
          }
          
          const paths = extractPaths(numbers);
          setAvailablePaths(paths);
        } catch (numbersErr: any) {
          if (numbersErr?.response?.status === 404) {
            setError('No traffic data available yet (404). The dropdown options are dynamically generated from article metrics. Please wait for the latest article to receive traffic before configuring paths.');
          } else {
            setError('Error fetching article metrics. The dropdown options depend on current article data. Please try again later.');
          }
          console.error('Error fetching story numbers:', numbersErr);
        }
      } catch (err) {
        setError('Error fetching latest article. The dropdown options are dynamically generated from your latest article data. Please ensure articles are being published.');
        console.error('Error fetching latest post:', err);
      }
    };
    fetchAvailablePaths();
  }, [api, brandId, extractPaths]);

  // Update config when data prop changes, used for logbook page
  useEffect(() => {
    const articleValueData = (data as ArticleValueDataWrapper)?.value || data as ArticleValueConfig;
    if (articleValueData && (articleValueData.sections?.length > 0 || articleValueData.totalScorePath)) {
      setConfig({
        totalScorePath: articleValueData.totalScorePath || '',
        sections: articleValueData.sections || []
      });
    }
  }, [data]);

  const pathOptions = useMemo(() => {
    // Deduplicate paths and add unique keys
    const uniquePaths = Array.from(new Set(availablePaths));
    return uniquePaths.map(path => ({ key: path, value: path, label: path }));
  }, [availablePaths]);

  // Get all existing dataSource values from sections and subsections
  const existingDataSources = useMemo(() => {
    const sources = new Set<string>();
    config.sections.forEach(section => {
      if (section.dataSource) sources.add(section.dataSource);
      section.subSections.forEach(subsection => {
        if (subsection.dataSource) sources.add(subsection.dataSource);
      });
    });
    return Array.from(sources);
  }, [config.sections]);

  // Extract label from path (last segment)
  const getLabelFromPath = (path: string): string => {
    const segments = path.split('.');
    return segments[segments.length - 1];
  };

  const handleInputChange = (value: any, field: keyof Section | string) => {
    const updates: any = { [field]: value };
    // Auto-set label when path changes
    if (field === 'path' && value) {
      updates.label = getLabelFromPath(value);
    }
    setSectionEditData(Object.assign({}, sectionEditData, updates));
  };

  const handleSubSectionChange = (index: number, field: keyof SubSection, value: any) => {
    const updatedSubSections = sectionEditData.subSections.slice();
    const updates: any = { [field]: value };
    // Auto-set label when path changes
    if (field === 'path' && value) {
      updates.label = getLabelFromPath(value);
    }
    updatedSubSections[index] = Object.assign({}, updatedSubSections[index], updates);
    setSectionEditData(Object.assign({}, sectionEditData, {
      subSections: updatedSubSections
    }));
  };

  const handleAddSubSection = () => {
    setSectionEditData(Object.assign({}, sectionEditData, {
      subSections: sectionEditData.subSections.concat(Object.assign({}, emptySubSection))
    }));
  };

  const handleDeleteSubSection = (index: number) => {
    const updatedSubSections = sectionEditData.subSections.filter((_, i) => i !== index);
    setSectionEditData(Object.assign({}, sectionEditData, {
      subSections: updatedSubSections
    }));
  };

  const handleSectionSave = () => {
    if (!sectionEditData.label || !sectionEditData.path) {
      setError("Label and Path are required fields");
      return;
    }

    if (!sectionEditData.dataSource || !sectionEditData.dataSource.trim()) {
      setError("DataSource is required for all sections");
      return;
    }

    if (sectionEditData.minRange >= sectionEditData.maxRange) {
      setError("Minimum range must be less than maximum range");
      return;
    }

    setError(null);

    const updatedSections = config.sections.slice();

    if (sectionEditIndex !== null && sectionEditIndex >= 0) {
      updatedSections[sectionEditIndex] = sectionEditData;
    } else {
      updatedSections.push(sectionEditData);
    }

    const updatedConfig = Object.assign({}, config, {
      sections: updatedSections
    });

    setConfig(updatedConfig);
    onSave(updatedConfig);
    setSectionEditIndex(null);
    
    // Show saved message for 3 seconds
    setSectionSaved(true);
    setTimeout(() => {
      setSectionSaved(false);
    }, 3000);
  };

  const handleSectionCancel = () => {
    if (sectionEditIndex === -1 && sectionEditData.label === '') {
      const updatedSections = config.sections.filter(s => s.label !== '');
      setConfig(Object.assign({}, config, { sections: updatedSections }));
    }
    setSectionEditIndex(null);
    setError(null);
  };

  const handleSectionDelete = (index: number) => {
    const confirmed = window.confirm("Are you sure you want to delete this section?");
    if (!confirmed) {
      return;
    }

    setError(null);

    const updatedSections = config.sections.filter((_, i) => i !== index);
    const updatedConfig = Object.assign({}, config, {
      sections: updatedSections
    });

    setConfig(updatedConfig);
    onSave(updatedConfig);
    
    // Show saved message for 3 seconds
    setSectionSaved(true);
    setTimeout(() => {
      setSectionSaved(false);
    }, 3000);
  };

  const handleSectionNew = () => {
    const newSection = Object.assign({}, emptySection);
    setSectionEditData(newSection);
    setSectionEditIndex(-1);

    setTimeout(() => {
      if (sectionBodyRef.current) {
        sectionBodyRef.current.scrollTop = sectionBodyRef.current.scrollHeight;
      }
    }, 100);
  };

  const handleSave = () => {
    onSave(config);
    onClose();
  };

  const handleTotalScorePathChange = (value: string) => {
    const updatedConfig = {
      ...config,
      totalScorePath: value
    };
    setConfig(updatedConfig);
    onSave(updatedConfig);
    
    // Show saved message for 3 seconds
    setTotalScorePathSaved(true);
    setTimeout(() => {
      setTotalScorePathSaved(false);
    }, 3000);
  };

  const handleBulkDataSourceApply = () => {
    if (!bulkDataSource.trim()) {
      setError("Please enter a dataSource value to apply");
      return;
    }

    // Apply dataSource to all sections and subsections
    const updatedSections = config.sections.map(section => ({
      ...section,
      dataSource: bulkDataSource,
      subSections: section.subSections.map(subsection => ({
        ...subsection,
        dataSource: bulkDataSource
      }))
    }));

    const updatedConfig = {
      ...config,
      sections: updatedSections
    };

    setConfig(updatedConfig);
    onSave(updatedConfig);
    setError(null);

    // Show saved message for 3 seconds
    setBulkDataSourceSaved(true);
    setTimeout(() => {
      setBulkDataSourceSaved(false);
    }, 3000);
  };

  return (
    <div className={`av-modal-wrapper ${hideActions ? "full-width" : ""}`}>
      <h3>Article Value Configuration - {brandName}</h3>
      <p className="av-description">
        Configure article value scoring sections and their subsections. Each section defines a score range and metrics.
      </p>

      {error && <div className="error-message">{error}</div>}

      <div className="top-settings-row">
        <div className="total-score-path-container">
          <label>Total Score Object path</label>
          <Select
            selectedOption={config.totalScorePath}
            setSelectedOption={handleTotalScorePathChange}
            options={pathOptions}
            fullWidth={true}
          />
          {totalScorePathSaved && (
            <div className="saved-message">Saved</div>
          )}
        </div>

        <div className="bulk-datasource-container">
          <label>Apply DataSource to All Sections & Subsections</label>
          <div className="bulk-datasource-controls">
            <ComboSelect
              options={existingDataSources}
              selectedOption={bulkDataSource}
              setSelectedOption={setBulkDataSource}
              placeholder="Select or type new dataSource..."
              fullWidth={false}
            />
            <Button
              variant="success"
              size="small"
              onClick={handleBulkDataSourceApply}
              disabled={!bulkDataSource.trim()}
            >
              Apply to All
            </Button>
            {bulkDataSourceSaved && (
              <div className="saved-message">Applied & Saved</div>
            )}
          </div>
          <p className="bulk-datasource-hint">
            This will override all existing dataSource values in all sections and subsections.
          </p>
        </div>
      </div>

      <div className="sections-container">
        <div className="section-header">
          <div className="section-label">
            <div className="header-with-tooltip">
              <p>Section Label</p>
              <span className="tooltip-wrapper" title="The section title can be changed, but if it relates to a custom metric, leave it as it is, as the name specified in the configured custom metric will be used.">
                <QuestionMarkIcon />
              </span>
            </div>
          </div>
          <div className="section-path">
            <p>Object path</p>
          </div>
          <div className="section-range">
            <p>Range (Min-Max)</p>
          </div>
          <div className="section-range-paths">
            <p>Min/Max Paths</p>
          </div>
          <div className="section-datasource">
            <p>DataSource</p>
          </div>
          <div className="section-subsections">
            <p>Subsections</p>
          </div>
          <div className="section-buttons">
            {sectionSaved && (
              <div className="saved-message" style={{ position: 'relative', left: 'auto', top: 'auto', transform: 'none' }}>Saved</div>
            )}
          </div>
        </div>
        <div 
          className={`section-body ${sectionEditIndex === config.sections.length - 1 ? 'expanded-for-last' : ''}`}
          ref={sectionBodyRef}
        >
          {config.sections.length === 0 && sectionEditIndex === null ? (
            <div className="empty-message">No sections configured. Click + to add one.</div>
          ) : (
            <>
              {config.sections.map((section, key) => (
                <div className="section" key={key}>
                  <div className="section-label">
                    {sectionEditIndex === key ? (
                      <Input
                        type="text"
                        value={sectionEditData?.label}
                        onChange={(e) => handleInputChange(e.target.value, "label")}
                        placeholder="e.g., volume_score"
                      />
                    ) : (
                      <p>{section.label}</p>
                    )}
                  </div>
                  <div className="section-path">
                    {sectionEditIndex === key ? (
                      <Select
                        options={pathOptions}
                        selectedOption={sectionEditData?.path}
                        setSelectedOption={(value) => handleInputChange(value, "path")}
                        fullWidth={true}
                      />
                    ) : (
                      <p>{section.path}</p>
                    )}
                  </div>
                  <div className="section-range">
                    {sectionEditIndex === key ? (
                      <div className="range-inputs">
                        <div className="range-input-group">
                          <label>Min:</label>
                          <input
                            type="number"
                            min="0"
                            value={sectionEditData.minRange}
                            onChange={(e) => handleInputChange(parseInt(e.target.value) || 0, "minRange")}
                            className="range-number-input"
                          />
                        </div>
                        <div className="range-input-group">
                          <label>Max:</label>
                          <input
                            type="number"
                            min="1"
                            value={sectionEditData.maxRange}
                            onChange={(e) => handleInputChange(parseInt(e.target.value) || 1, "maxRange")}
                            className="range-number-input"
                          />
                        </div>
                      </div>
                    ) : (
                      <p>{section.minRange} - {section.maxRange}</p>
                    )}
                  </div>
                  <div className="section-range-paths">
                    {sectionEditIndex === key ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <Select
                          options={pathOptions}
                          selectedOption={sectionEditData?.minPath}
                          setSelectedOption={(value) => handleInputChange(value, "minPath")}
                          fullWidth={true}
                        />
                        <Select
                          options={pathOptions}
                          selectedOption={sectionEditData?.maxPath}
                          setSelectedOption={(value) => handleInputChange(value, "maxPath")}
                          fullWidth={true}
                        />
                      </div>
                    ) : (
                      <div style={{ fontSize: '12px' }}>
                        <div>Min: {section.minPath}</div>
                        <div>Max: {section.maxPath}</div>
                      </div>
                    )}
                  </div>
                  <div className="section-datasource">
                    {sectionEditIndex === key ? (
                      <ComboSelect
                        options={existingDataSources}
                        selectedOption={sectionEditData?.dataSource}
                        setSelectedOption={(value) => handleInputChange(value, "dataSource")}
                        placeholder="Select or add..."
                        fullWidth={true}
                        required={true}
                      />
                    ) : (
                      <p>{section.dataSource || <span style={{ color: '#999', fontStyle: 'italic' }}>Not set</span>}</p>
                    )}
                  </div>
                  <div className="section-subsections">
                    <div style={{ fontSize: '12px' }}>
                      {section.subSections.length > 0 ? (
                        <div>{section.subSections.length} subsection{section.subSections.length !== 1 ? 's' : ''}</div>
                      ) : (
                        <div style={{ color: '#999' }}>No subsections</div>
                      )}
                    </div>
                  </div>
                  <div className="section-buttons">
                    <Button
                      variant="success"
                      size="small"
                      title="Save"
                      onClick={handleSectionSave}
                      style={{ display: sectionEditIndex === key ? "" : "none" }}
                    >
                      <SaveIcon />
                    </Button>

                    <Button
                      variant="warning"
                      size="small"
                      title="Cancel"
                      onClick={handleSectionCancel}
                      style={{
                        marginLeft: "10px",
                        marginRight: "10px",
                        display: sectionEditIndex === key ? "" : "none",
                      }}
                    >
                      <CloseIcon />
                    </Button>

                    <Button
                      variant="warning"
                      size="small"
                      title="Edit"
                      onClick={() => {
                        setSectionEditData(section);
                        setSectionEditIndex(key);

                        setTimeout(() => {
                          const sectionElement = sectionBodyRef.current?.children[key] as HTMLElement;
                          if (sectionElement && sectionBodyRef.current) {
                            const elementTop = sectionElement.offsetTop;
                            const containerHeight = sectionBodyRef.current.clientHeight;
                            const scrollPosition = elementTop - containerHeight + sectionElement.clientHeight + 200;
                            sectionBodyRef.current.scrollTop = Math.max(0, scrollPosition);
                          }
                        }, 100);
                      }}
                      style={{ display: sectionEditIndex === key ? "none" : "" }}
                    >
                      <EditIcon />
                    </Button>
                    <Button
                      variant="warning"
                      size="small"
                      title="Edit Subsections"
                      onClick={() => {
                        setSubsectionParentIndex(key);
                        setSectionEditData(section);
                        setSubsectionEditMode(true);
                      }}
                      style={{ display: sectionEditIndex === key ? "none" : "" }}
                      className="edit-subsections-btn"
                    >
                      <EditIcon />
                    </Button>
                    <Button
                      variant="danger"
                      size="small"
                      title="Delete"
                      style={{
                        marginLeft: "10px",
                        marginRight: "10px",
                        display: sectionEditIndex === key ? "none" : "",
                      }}
                      onClick={() => handleSectionDelete(key)}
                    >
                      <DeleteIcon />
                    </Button>
                  </div>
                </div>
              ))}
              {sectionEditIndex === -1 && (
                <div className="section">
                  <div className="section-label">
                    <Input
                      type="text"
                      value={sectionEditData?.label}
                      onChange={(e) => handleInputChange(e.target.value, "label")}
                      placeholder="e.g., volume_score"
                    />
                  </div>
                  <div className="section-path">
                    <Select
                      options={pathOptions}
                      selectedOption={sectionEditData?.path}
                      setSelectedOption={(value) => handleInputChange(value, "path")}
                      fullWidth={true}
                    />
                  </div>
                  <div className="section-range">
                    <div className="range-inputs">
                      <div className="range-input-group">
                        <label>Min:</label>
                        <input
                          type="number"
                          min="1"
                          value={sectionEditData.minRange}
                          onChange={(e) => handleInputChange(parseInt(e.target.value) || 1, "minRange")}
                          className="range-number-input"
                        />
                      </div>
                      <div className="range-input-group">
                        <label>Max:</label>
                        <input
                          type="number"
                          min="1"
                          value={sectionEditData.maxRange}
                          onChange={(e) => handleInputChange(parseInt(e.target.value) || 1, "maxRange")}
                          className="range-number-input"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="section-range-paths">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                      <Select
                        options={pathOptions}
                        selectedOption={sectionEditData?.maxPath}
                        setSelectedOption={(value) => handleInputChange(value, "maxPath")}
                        fullWidth={true}
                      />
                      <Select
                        options={pathOptions}
                        selectedOption={sectionEditData?.minPath}
                        setSelectedOption={(value) => handleInputChange(value, "minPath")}
                        fullWidth={true}
                      />
                    </div>
                  </div>
                  <div className="section-datasource">
                    <ComboSelect
                      options={existingDataSources}
                      selectedOption={sectionEditData?.dataSource}
                      setSelectedOption={(value) => handleInputChange(value, "dataSource")}
                      placeholder="Select or add..."
                      fullWidth={true}
                      required={true}
                    />
                  </div>
                  <div className="section-subsections">
                    <div style={{ fontSize: '12px', fontStyle: 'italic', color: '#999' }}>
                      Add subsections after saving
                    </div>
                  </div>
                  <div className="section-buttons">
                    <Button
                      variant="success"
                      size="small"
                      title="Save"
                      onClick={handleSectionSave}
                    >
                      <SaveIcon />
                    </Button>

                    <Button
                      variant="warning"
                      size="small"
                      title="Cancel"
                      onClick={handleSectionCancel}
                      style={{
                        marginLeft: "10px",
                        marginRight: "10px",
                      }}
                    >
                      <CloseIcon />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        <div className="section-footer">
          <Button
            variant="success"
            size="small"
            title={sectionEditIndex !== null ? "First save or cancel editing" : "Add new section"}
            style={{ marginLeft: "46%", marginRight: "47%" }}
            onClick={handleSectionNew}
            disabled={sectionEditIndex !== null}
            className={sectionEditIndex !== null ? "disabled-with-icon" : ""}
          >
            {sectionEditIndex !== null ? <CloseIcon color="#999" /> : <PlusIcon />}
          </Button>
        </div>
      </div>

      {subsectionEditMode && subsectionParentIndex !== null && (
        <div className="subsections-edit-container">
          <h4>Edit Subsections for: {config.sections[subsectionParentIndex]?.label}</h4>
          <div className="subsections-list">
            {sectionEditData.subSections.map((subSection, subIndex) => (
              <div key={subIndex} className="subsection-edit-row">
                <div className="subsection-field">
                  <label>Label:</label>
                  <Input
                    type="text"
                    value={subSection.label}
                    onChange={(e) => handleSubSectionChange(subIndex, 'label', e.target.value)}
                    placeholder="Label"
                  />
                </div>
                <div className="subsection-field">
                  <label>Path:</label>
                  <Select
                    options={pathOptions}
                    selectedOption={subSection.path}
                    setSelectedOption={(value) => handleSubSectionChange(subIndex, 'path', value)}
                    fullWidth={true}
                  />
                </div>
                <div className="subsection-field">
                  <label>Format:</label>
                  <Select
                    options={formatOptions}
                    selectedOption={subSection.format}
                    setSelectedOption={(value) => handleSubSectionChange(subIndex, 'format', value)}
                    fullWidth={true}
                  />
                </div>
                <div className="subsection-field">
                  <label>DataSource:</label>
                  <ComboSelect
                    options={existingDataSources}
                    selectedOption={subSection.dataSource}
                    setSelectedOption={(value) => handleSubSectionChange(subIndex, 'dataSource', value)}
                    placeholder="Select or add..."
                    fullWidth={true}
                    required={true}
                  />
                </div>
                <Button
                  variant="danger"
                  size="small"
                  onClick={() => handleDeleteSubSection(subIndex)}
                  title="Delete subsection"
                >
                  <DeleteIcon />
                </Button>
              </div>
            ))}
            <Button
              variant="success"
              size="small"
              onClick={handleAddSubSection}
              className="add-subsection-btn"
            >
              <PlusIcon /> Add Subsection
            </Button>
          </div>
          <div className="subsection-actions">
            {subsectionSaved && (
              <div className="saved-message" style={{ position: 'relative', left: 'auto', top: 'auto', transform: 'none', marginRight: 'auto' }}>Saved</div>
            )}
            <Button
              variant="danger"
              onClick={() => {
                setSubsectionEditMode(false);
                setSubsectionParentIndex(null);
                setSectionEditData(emptySection);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="success"
              onClick={() => {
                // Validate all subsections have required fields
                const invalidSubsections = sectionEditData.subSections.filter(
                  sub => !sub.label || !sub.path || !sub.dataSource || !sub.dataSource.trim()
                );
                
                if (invalidSubsections.length > 0) {
                  setError("All subsections must have Label, Path, and DataSource filled in");
                  return;
                }
                
                if (subsectionParentIndex !== null) {
                  const updatedSections = config.sections.slice();
                  updatedSections[subsectionParentIndex] = Object.assign({}, sectionEditData);
                  const updatedConfig = Object.assign({}, config, { sections: updatedSections });
                  setConfig(updatedConfig);
                  onSave(updatedConfig);
                  setSubsectionEditMode(false);
                  setSubsectionParentIndex(null);
                  setSectionEditData(emptySection);
                  setError(null);
                  
                  // Show saved message for 3 seconds
                  setSubsectionSaved(true);
                  setTimeout(() => {
                    setSubsectionSaved(false);
                  }, 3000);
                }
              }}
            >
              Save Subsections
            </Button>
          </div>
        </div>
      )}

      {!hideActions && (
        <div className="sub-modal-actions">
          <Button
            variant="danger"
            onClick={() => {
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={() => {
              handleSave();
              onClose();
            }}
          >
            OK
          </Button>
        </div>
      )}
    </div>
  );
};

export default ArticleValueModal;
